import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GavelIcon from '@mui/icons-material/Gavel';
import LogoutIcon from '@mui/icons-material/Logout';
import { usePrestadorAuth, prestadorFetch } from '../contexts/PrestadorAuthContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PrestadorPropostaEditalDialog from '../components/prestador/PrestadorPropostaEditalDialog';
import PrestadorContratosGrid from '../components/prestador/PrestadorContratosGrid';
import PrestadorGraficosPanel from '../components/prestador/PrestadorGraficosPanel';
import PrestadorNotasHistorico from '../components/prestador/PrestadorNotasHistorico';

export default function PrestadorDashboardPage() {
  const { prestador, logoutPrestador } = usePrestadorAuth();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingContratos, setLoadingContratos] = useState(false);
  const [data, setData] = useState(null);
  const [contratos, setContratos] = useState([]);
  const [graficos, setGraficos] = useState(null);
  const [loadingGraficos, setLoadingGraficos] = useState(false);
  const [editalDialog, setEditalDialog] = useState({ open: false, edital: null });

  const carregarDashboard = useCallback(() => {
    setLoading(true);
    return prestadorFetch('/api/prestador/dashboard')
      .then(async (r) => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          console.error('[prestador/dashboard]', r.status, err.error || r.statusText);
          return null;
        }
        return r.json();
      })
      .then((d) => {
        setData(d);
        // Garante lista de contratos mesmo se /contratos falhar
        if (Array.isArray(d?.contratos) && d.contratos.length) {
          setContratos((prev) => (prev?.length ? prev : d.contratos));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const carregarContratos = useCallback(() => {
    setLoadingContratos(true);
    return prestadorFetch('/api/prestador/contratos')
      .then(async (r) => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          console.error('[prestador/contratos]', r.status, err.error || r.statusText);
          return null; // não limpa lista existente / seed
        }
        return r.json();
      })
      .then((lista) => {
        if (Array.isArray(lista)) setContratos(lista);
      })
      .finally(() => setLoadingContratos(false));
  }, []);

  const carregarGraficos = useCallback(() => {
    setLoadingGraficos(true);
    return prestadorFetch('/api/prestador/graficos')
      .then((r) => (r.ok ? r.json() : null))
      .then((g) => setGraficos(g))
      .finally(() => setLoadingGraficos(false));
  }, []);

  useEffect(() => {
    try {
      const seed = sessionStorage.getItem('godModeContratosSeed');
      if (seed) {
        const parsed = JSON.parse(seed);
        if (Array.isArray(parsed) && parsed.length) {
          setContratos(parsed);
        }
        sessionStorage.removeItem('godModeContratosSeed');
      }
    } catch {
      sessionStorage.removeItem('godModeContratosSeed');
    }
    carregarDashboard();
    carregarContratos();
    carregarGraficos();
  }, [carregarDashboard, carregarContratos, carregarGraficos]);

  const handleLogout = () => {
    logoutPrestador();
    navigate(user ? '/configuracao' : '/login');
  };

  const abrirContrato = (c) => {
    navigate(`/prestador/contratos/${encodeURIComponent(c.numeroContrato)}`);
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  const qtdContratos = contratos.length || data?.resumo?.contratosAtivos || 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(66,165,245,0.08) 100%)',
          border: '1px solid rgba(25,118,210,0.15)'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="primary">
              Portal do Prestador
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {prestador?.razaoSocial || prestador?.nomeFantasia}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              CNPJ: {prestador?.cnpj} · {prestador?.email}
            </Typography>
            <Chip label={prestador?.status || 'ATIVO'} color="success" size="small" sx={{ mt: 1 }} />
            {prestador?.godMode && (
              <Chip label="God Mode" color="warning" size="small" sx={{ mt: 1, ml: 1 }} />
            )}
          </Box>
          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ textTransform: 'none' }}
          >
            Sair
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
            <BusinessIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>{qtdContratos}</Typography>
            <Typography variant="caption" color="text.secondary">Contratos ativos</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
            <GavelIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>{data?.resumo?.editaisAbertos ?? 0}</Typography>
            <Typography variant="caption" color="text.secondary">Editais no prazo</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
            <AssignmentIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>{data?.resumo?.propostasTotal ?? 0}</Typography>
            <Typography variant="caption" color="text.secondary">Propostas enviadas</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={700} color="primary">
              {graficos?.resumo?.totalImpostos != null
                ? Number(graficos.resumo.totalImpostos).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                    maximumFractionDigits: 0
                  })
                : '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">Impostos (NF)</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`Meus contratos (${qtdContratos})`} sx={{ textTransform: 'none' }} />
          <Tab label="Notas fiscais" sx={{ textTransform: 'none' }} />
          <Tab label="Editais abertos" sx={{ textTransform: 'none' }} />
          <Tab label="Minhas propostas" sx={{ textTransform: 'none' }} />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            <PrestadorContratosGrid
              contratos={contratos}
              loading={loadingContratos}
              onSelectContrato={abrirContrato}
            />
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <PrestadorNotasHistorico />
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Editais dentro do prazo. Após encerramento não é possível enviar documentação. Segundo chamamento
              aparece como novo edital com datas atualizadas.
            </Typography>
            {!data?.editaisAbertos?.length ? (
              <Typography color="text.secondary">Nenhum edital aberto no momento.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Título</TableCell>
                    <TableCell>Encerramento</TableCell>
                    <TableCell>Processo</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.editaisAbertos.map((e) => (
                    <TableRow key={e.id || e.codigo}>
                      <TableCell>
                        {e.codigo}
                        {e.chamamento > 1 && (
                          <Chip size="small" label={`${e.chamamento}º cham.`} sx={{ ml: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>{e.titulo}</TableCell>
                      <TableCell>{e.data_encerramento || '—'}</TableCell>
                      <TableCell>{e.processo_codigo || '—'}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setEditalDialog({ open: true, edital: e })}
                          sx={{ textTransform: 'none' }}
                        >
                          Proposta e documentos
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {tab === 3 && (
          <Box sx={{ p: 2 }}>
            {!data?.propostas?.length ? (
              <Typography color="text.secondary">Você ainda não enviou propostas.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Edital</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.propostas.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.edital_codigo}</TableCell>
                      <TableCell>
                        {p.valor != null ? `R$ ${Number(p.valor).toLocaleString('pt-BR')}` : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={p.status} />
                      </TableCell>
                      <TableCell>
                        {p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </Paper>

      <PrestadorPropostaEditalDialog
        open={editalDialog.open}
        edital={editalDialog.edital}
        onClose={() => setEditalDialog({ open: false, edital: null })}
        onEnviado={() => {
          carregarDashboard();
          carregarContratos();
          carregarGraficos();
        }}
      />
    </Box>
  );
}
